/**
 * Small shared presentational components used across the dashboard routes.
 * Intentionally dependency-free (no UI kit) — plain elements + class names that
 * the global stylesheet styles.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import type { TicketStatus } from '@hotline/contracts';

import { Alert } from './ui/Alert.js';

export function Spinner({ label = 'Loading…' }: { label?: string }): ReactElement {
  return (
    <div className="spinner" role="status" aria-live="polite">
      {label}
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }): ReactElement {
  return <Alert>{message}</Alert>;
}

export function EmptyState({ children }: { children: ReactNode }): ReactElement {
  return <div className="empty-state">{children}</div>;
}

/** A labeled status pill (open / triaged / resolved / …). */
export function StatusBadge({ status }: { status: TicketStatus }): ReactElement {
  return (
    <span className={`badge badge-status badge-${status}`} data-status={status}>
      {status.replace('_', ' ')}
    </span>
  );
}

/** A labeled form field wrapper. */
export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: ReactNode;
}): ReactElement {
  return (
    <div className="field">
      <label className="field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint ? <p className="field-hint">{hint}</p> : null}
    </div>
  );
}

/**
 * A read-only code block with a copy-to-clipboard button. Used for install
 * snippets, the CSP block, and the one-time secret key reveal.
 */
export function CopyBlock({
  code,
  language = 'text',
  label,
}: {
  code: string;
  language?: string;
  label?: string;
}): ReactElement {
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard?.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div
      className="my-2 overflow-hidden rounded-md border border-border bg-surface-2"
      data-language={language}
    >
      <div className="flex items-center justify-between border-b border-border px-2.5 py-1.5">
        {label ? <span className="text-xs text-muted">{label}</span> : <span />}
        <button
          type="button"
          className="cursor-pointer rounded text-xs text-muted hover:text-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          onClick={copy}
        >
          {copied ? 'Copied!' : 'Copy'}
        </button>
      </div>
      <pre className="m-0 overflow-x-auto px-3 py-3 text-[13px]">
        <code>{code}</code>
      </pre>
    </div>
  );
}
